import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { FileTypeUtils } from '@repo/lib/fs/fileTypeUtils';
import { MessageReadReceipts, MessageSend } from '@repo/types/message';
import { UserPublic } from '@repo/types/users';
import { format } from 'date-fns';
import { CopyIcon, Download, EyeIcon, Reply, Trash, UserIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { LinkItEmail, LinkItUrl } from 'react-linkify-it';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupButtonMore, AvatarGroupContent, AvatarImage } from '@/components/ui/avatar';
import { ContextMenu, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MessageBubble,
  MessageBubbleAudio,
  MessageBubbleAvatar,
  MessageBubbleContainer,
  MessageBubbleContent,
  MessageBubbleFile,
  MessageBubbleFilePDF,
  MessageBubbleFooter,
  MessageBubbleImage,
  MessageBubbleMessage,
  MessageBubbleReply,
  MessageBubbleUsername,
  MessageBubbleVideo,
} from '@/components/ui/message-bubble';
import { useCopyToClipboard } from '@/hooks/copy-to-clipboard';
import { chatSocketService } from '@/services/chat-service';
import { useCredentials } from '@/store/auth-store';
import { useChatStore } from '@/store/chat-store';
import { getFileUrl } from '@/utils/file/getFileUrl';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for MessageCard component.
 * @property {MessageSend} message - The message data to display.
 * @property {...ComponentProps<'div'>} - Additional div props.
 */
interface MessageCardProps extends ComponentProps<'div'> {
  message: MessageSend;
  stackedReadReceipts?: MessageReadReceipts[];
  previousMessage?: MessageSend;
}

const STACKED_READ_RECEIPTS_LIMIT = 5;

export default function MessageCard(messageCardProps: MessageCardProps) {
  const { message, className, stackedReadReceipts = [], previousMessage, ...props } = messageCardProps;
  const { tempUUID, sender, createdAt, id, content, replyingTo, type, status } = message;

  const [open, onOpenChange] = useState<boolean>(false);
  const { id: userId } = useCredentials();

  const isUserMessage: boolean = sender?.id === userId;
  const isPrimary: boolean = isUserMessage && status !== 'error';

  const readReceipts = useMemo<MessageReadReceipts[]>(() => {
    // Our own receipt is recorded on every message we scroll past; the list is
    // about who else has read it.
    return (message.readReceipts ?? []).filter((receipt) => receipt.id !== userId);
  }, [message.readReceipts, userId]);

  const hasVisibleOtherReaders = useMemo<boolean>(() => {
    return stackedReadReceipts.length > 0;
  }, [stackedReadReceipts]);

  const overflowCount = useMemo<number>(() => {
    return hasVisibleOtherReaders && stackedReadReceipts.length > STACKED_READ_RECEIPTS_LIMIT ? stackedReadReceipts.length - STACKED_READ_RECEIPTS_LIMIT : 0;
  }, [hasVisibleOtherReaders, stackedReadReceipts]);

  const isStackedWithPrevious = useMemo<boolean>(() => {
    if (!previousMessage?.sender?.id || !sender?.id || !createdAt || !previousMessage?.createdAt) {
      return false;
    }

    const isSameSender: boolean = previousMessage.sender.id === sender.id;
    const timeDiff: number = Math.abs(new Date(createdAt).getTime() - new Date(previousMessage.createdAt).getTime());

    return isSameSender && timeDiff <= 60 * 1000;
  }, [previousMessage, createdAt, sender]);

  const getName = (sender?: UserPublic): string => {
    if (sender) {
      if (sender.id === userId) {
        return 'Ty';
      }
      const surnameInitial: string = sender.surname ? sender.surname.charAt(0) : '';
      const formattedSurname: string = surnameInitial ? `${surnameInitial}.` : '';
      return [sender.name, formattedSurname].filter(Boolean).join(' ');
    } else {
      return 'Użytkownik został usunięty';
    }
  };

  return (
    <div
      className={cn(
        'flex w-full items-center justify-start gap-2 select-none first:mt-0',
        className,
        isUserMessage ? 'flex-row-reverse self-end' : 'flex-row self-start',
        isStackedWithPrevious ? 'mt-1.5' : 'mt-4'
      )}
      id={String(id)}
      data-message-date={createdAt}
      data-message-sender={sender?.id}
      {...props}
    >
      <ChatMessageMoreContext message={message} openReadReceipts={() => onOpenChange(true)} isUserMessage={isUserMessage}>
        <div className={cn('flex flex-col gap-0.5', isUserMessage ? 'items-end' : 'items-start')}>
          <MessageBubble key={message.id} variant={isPrimary ? 'primary' : 'secondary'} side={isUserMessage ? 'end' : 'start'} className={type === 'deleted' ? 'opacity-75' : ''}>
            <MessageBubbleContainer className="max-w-2xl">
              {!isStackedWithPrevious && (
                <div className={cn('flex items-center gap-1', isUserMessage ? 'flex-row-reverse pe-2' : 'flex-row ps-2')}>
                  <MessageBubbleAvatar avatarURL={sender?.avatarURL ?? undefined} className={'mt-0 size-3.5 shrink-0'} />
                  <MessageBubbleUsername username={getName(sender)} className="flex-1" />
                </div>
              )}
              <MessageBubbleContent>
                {replyingTo && (
                  <MessageBubbleReply content={replyingTo.content ?? ''} username={getName(replyingTo.sender)}>
                    <MessageMedia message={replyingTo} />
                  </MessageBubbleReply>
                )}
                <MessageMedia message={message} />
                {content && (
                  <MessageBubbleMessage>
                    <LinkItUrl className="font-medium underline">
                      <LinkItEmail className="font-medium underline">{content}</LinkItEmail>
                    </LinkItUrl>
                  </MessageBubbleMessage>
                )}
                <MessageBubbleFooter createdAt={message.createdAt} isPending={status === 'pending'} isError={status === 'error'} onRetry={() => chatSocketService.retryMessage(tempUUID)} />
              </MessageBubbleContent>
              {hasVisibleOtherReaders && (
                <AvatarGroup onClick={() => onOpenChange(true)} className={cn(isUserMessage ? 'ms-auto' : 'me-auto')}>
                  <AvatarGroupContent>
                    {stackedReadReceipts.slice(0, STACKED_READ_RECEIPTS_LIMIT).map((user) => (
                      <Avatar key={user.id} size={'xs'}>
                        <AvatarImage src={getAvatar(user.avatarURL)} alt={`${user.name} ${user.surname}`} />
                        <AvatarFallback>
                          <UserIcon />
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {overflowCount > 0 && <AvatarGroupButtonMore className={'h-4.5 w-6 text-xs'}>+{overflowCount}</AvatarGroupButtonMore>}
                  </AvatarGroupContent>
                </AvatarGroup>
              )}
            </MessageBubbleContainer>
          </MessageBubble>
          <ReadReceiptsDialog open={open} onOpenChange={onOpenChange} readReceipts={readReceipts} />
        </div>
      </ChatMessageMoreContext>
    </div>
  );
}

interface ReadReceiptsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readReceipts: MessageReadReceipts[];
}

export function ReadReceiptsDialog(readReceiptsDialogProps: ReadReceiptsDialogProps) {
  const { open, onOpenChange, readReceipts } = readReceiptsDialogProps;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'md:max-w-96'}>
        <DialogBody>
          <DialogHeader className={'border-border-primary flex-col border-b'}>
            <DialogTitle>Odczytane przez</DialogTitle>
            <DialogDescription className="sr-only">Wiadomość została odczytana przez poniższych użytkowników</DialogDescription>
          </DialogHeader>
          <div className={'flex flex-col gap-6 overflow-auto p-4'}>
            <div className={'flex-1 overflow-y-auto'}>
              {readReceipts.length > 0 ? (
                readReceipts.map((receipt) => (
                  <div key={receipt.id} className={'group flex items-center justify-between gap-3 overflow-hidden rounded-sm py-2'}>
                    <Avatar size={'md'}>
                      <AvatarImage src={getAvatar(receipt.avatarURL)} alt={'avatar'} />
                      <AvatarFallback>
                        <UserIcon />
                      </AvatarFallback>
                    </Avatar>
                    <div className={'flex w-full flex-col gap-0.5 overflow-hidden'}>
                      <p className={'text-text-secondary w-full truncate text-sm'}>
                        {receipt.name} {receipt.surname}
                      </p>
                      <small className={'text-text-tertiary text-xs'}>{format(receipt.messagereadreceipts.createdAt, 'dd MMM RRRR, HH:mm:ss')}</small>
                    </div>
                  </div>
                ))
              ) : (
                <p className={'text-text-tertiary py-12 text-center text-sm'}>Brak uczestników</p>
              )}
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Props for MessageMedia component.
 * @property {MessageSend} message - The message to display media for.
 */
type MessageMediaMessage = Pick<MessageSend, 'type' | 'content' | 'attachment' | 'file'>;

interface MessageMediaProps {
  message: MessageMediaMessage;
}

function MessageMedia(messageMediaProps: MessageMediaProps) {
  const { message } = messageMediaProps;
  const { type: _type, content, attachment, file } = message;

  const [fileURL, fileURLObject] = useMemo<[string | undefined, string | undefined]>(() => {
    if (attachment) {
      return [getFileUrl(attachment.id), undefined];
    } else if (file) {
      return [undefined, URL.createObjectURL(file)];
    }
    return [undefined, undefined];
  }, [file, attachment]);

  const onOpenModal = () => {
    useChatStore.getState().setViewedFile(file || attachment);
    useChatStore.getState().setOpenViewer(true);
  };

  const type = useMemo<string>(() => {
    // Only the PDF subtype has its own bubble (MessageBubbleFilePDF). Any
    // other "file" attachment must keep `_type='file'` so MessageBubbleFile
    // renders — returning a bare subtype like "plain" or "zip" matched no
    // switch case and silently dropped the bubble post-server-confirm.
    if (_type === 'file' && attachment?.fileType) {
      const { subtype } = FileTypeUtils.getMimeTypeStructure(attachment.fileType);
      if (subtype === 'pdf') return 'pdf';
    }
    return _type;
  }, [_type, attachment?.fileType]);

  const isPngImage = (src?: string | null): boolean => {
    if (!src) return false;
    return /\.png(\?|$)/i.test(src);
  };

  switch (type) {
    case 'image': {
      return (
        <MessageBubbleImage
          src={fileURL || fileURLObject || ''}
          alt={content ?? 'image'}
          className={cn(isPngImage(content) ? 'bg-checkered' : 'bg-background')}
          blurDataURL={attachment?.thumbnail ?? undefined}
          onClick={onOpenModal}
        />
      );
    }
    case 'video': {
      return <MessageBubbleVideo src={fileURL || fileURLObject || ''} title={attachment?.name ? attachment.name : 'video file'} onClick={onOpenModal} />;
    }
    case 'audio': {
      return <MessageBubbleAudio src={fileURL || fileURLObject || ''} />;
    }
    case 'pdf': {
      return <MessageBubbleFilePDF name={attachment?.name || file?.name} file={attachment ?? undefined} url={fileURL} onClick={onOpenModal} />;
    }
    case 'file': {
      return <MessageBubbleFile name={attachment?.name || file?.name} url={fileURL} />;
    }
    default:
      return null;
  }
}
/**
 * Props for ChatMessageMoreContext component.
 * @property {MessageSend} message - The message for context actions.
 * @property {boolean} isUserMessage - Is this message sent by the current user.
 * @property {ReactNode} children - Child elements.
 * @property {() => void} openReadReceipts - Callback to open read receipts.
 */
interface ChatMessageMoreContextProps {
  message: MessageSend;
  isUserMessage: boolean;
  children: ReactNode;
  openReadReceipts?: () => void;
}

function ChatMessageMoreContext(chatMessageMoreContextProps: ChatMessageMoreContextProps) {
  const { children, message, isUserMessage, openReadReceipts } = chatMessageMoreContextProps;
  const downloadButtonRef = useRef<HTMLAnchorElement>(null);
  const { id, type, attachment, content, status } = message;
  const [_, copy] = useCopyToClipboard();

  const onCopyText = async () => {
    if (!content) return;
    // useCopyToClipboard never throws — it resolves to `false` when
    // navigator.clipboard.writeText rejects. Branch on that instead of
    // try/catch so we actually surface the failure to the user.
    const ok = await copy(content);
    if (ok) {
      toast.success('Skopiowano do schowka', {
        duration: 1500,
        position: 'top-center',
      });
    } else {
      toast.error('Nie udało się skopiować do schowka', {
        duration: 1500,
        position: 'top-center',
      });
    }
  };

  const onDelete = () => {
    if (!id) return;
    chatSocketService.deleteMessage(id);
  };

  const onDownload = () => {
    if (!downloadButtonRef.current) return;
    downloadButtonRef.current.click();
  };

  const onReplyOn = () => {
    useChatStore.getState().setReplyingTo(message);
  };

  if (type === 'deleted' || status === ('error' as const) || status === ('pending' as const)) {
    return children;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuGroup>
          <ContextMenuItem onClick={onReplyOn} disabled={!id}>
            <Reply className={'size-4'} />
            Odpowiedz
          </ContextMenuItem>
          {type === 'text' ? (
            <ContextMenuItem onClick={onCopyText}>
              <CopyIcon aria-hidden="true" />
              Kopiuj tekst
            </ContextMenuItem>
          ) : null}
          {attachment ? (
            <ContextMenuItem onClick={onDownload}>
              <a ref={downloadButtonRef} href={getFileUrl(attachment.id)} className={'hidden'} />
              <Download className={'size-4'} />
              Pobierz
            </ContextMenuItem>
          ) : null}
          {openReadReceipts ? (
            <ContextMenuItem onClick={openReadReceipts}>
              <EyeIcon className={'size-4'} />
              Odczytane
            </ContextMenuItem>
          ) : null}
          {isUserMessage ? (
            <ContextMenuItem onClick={onDelete} disabled={!id} className={'text-text-destructive'}>
              <Trash className={'size-4'} />
              Usuń
            </ContextMenuItem>
          ) : null}
        </ContextMenuGroup>
      </ContextMenuContent>
    </ContextMenu>
  );
}
